import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// The pre-flight checklist, ported from the ibkr_api dashboard's
// PreflightPanel onto the console's design tokens. It reads the progressive
// report the nn-trainer image writes to NQUBE_PROGRESS_URL (mirrored to
// jobs/{id}.liveBlocks[block].progress) and, once the block is done, the
// block's output:
//   { currentCategory, checks: [{ category, name, status, detail }],
//     totalPassed, totalFailed, totalWarnings, totalChecks, completedAt,
//     verdict, estimatedUsd, estimatedHours, issues, suggestions }
// Checks reveal one at a time (the "pilot scan"); categories open while they
// are being verified and fold once confirmed; a person can expand any of
// them. No emoji, semantic HTML, status by colour + glyph + text.

const CATEGORY_ORDER = [
  'Environment', 'Stage artifacts', 'Feature column alignment', 'Feature computation',
  'Sequence building', 'Model training loop', 'Inference pipeline', 'Context window sufficiency',
  'Budget', 'Summary',
];
const CHECK_REVEAL_MS = 110;
const CAT_PAUSE_MS = 380;
const CAT_LINGER_MS = 900;

const toArr = (v) => {
  if (Array.isArray(v)) return v;
  if (v && typeof v === 'object') {
    const keys = Object.keys(v);
    if (keys.length && keys.every((k) => /^\d+$/.test(k))) return keys.sort((a, b) => Number(a) - Number(b)).map((k) => v[k]);
  }
  return [];
};
const sortKey = (cat) => { const i = CATEGORY_ORDER.indexOf(cat); return i >= 0 ? i : 999; };
const GLYPH = { pass: '✓', fail: '✕', warn: '!', pending: '·' };

function useProgressiveReveal(total, boundaries, instant) {
  const [count, setCount] = useState(instant ? total : 0);
  useEffect(() => { if (total === 0) setCount(0); }, [total]);
  useEffect(() => {
    if (instant) { setCount(total); return undefined; }
    if (count >= total) return undefined;
    const next = count + 1;
    const t = setTimeout(() => setCount((c) => Math.min(c + 1, total)), boundaries.has(next) ? CAT_PAUSE_MS : CHECK_REVEAL_MS);
    return () => clearTimeout(t);
  }, [count, total, boundaries, instant]);
  return Math.min(count, total);
}

function useAutoFold(cats, byCat, revealed) {
  const [auto, setAuto] = useState({});
  const prev = useRef(0);
  const timers = useRef({});
  useEffect(() => {
    let offset = 0;
    for (const cat of cats) {
      const start = offset;
      const end = offset + (byCat[cat] || []).length;
      offset = end;
      const was = prev.current > start && prev.current < end;
      const is = revealed > start && revealed < end;
      const justDone = revealed >= end && prev.current < end;
      if (is && !was) { clearTimeout(timers.current[cat]); setAuto((m) => ({ ...m, [cat]: true })); }
      if (justDone) {
        clearTimeout(timers.current[cat]);
        timers.current[cat] = setTimeout(() => setAuto((m) => ({ ...m, [cat]: false })), CAT_LINGER_MS);
      }
    }
    prev.current = revealed;
  }, [revealed, cats, byCat]);
  useEffect(() => { const t = timers.current; return () => Object.values(t).forEach(clearTimeout); }, []);
  return auto;
}

function Category({ name, checks, offset, total, revealed, expanded, onToggle, active }) {
  const listRef = useRef(null);
  const pass = checks.filter((c) => c.status === 'pass').length;
  const fail = checks.filter((c) => c.status === 'fail').length;
  const warn = checks.filter((c) => c.status === 'warn').length;
  const start = offset;
  const end = offset + checks.length;
  const begun = revealed > start;
  const full = revealed >= end && checks.length > 0;
  const verifying = begun && !full;
  const visible = checks.filter((_, i) => offset + i < revealed);
  useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [revealed]);
  const tone = !begun ? 'pending' : verifying ? 'verifying' : fail ? 'fail' : warn ? 'warn' : 'pass';
  return (
    <li className={`pf-cat is-${tone}${active && !full ? ' is-active' : ''}`}>
      <button type="button" className="pf-cat-head" onClick={onToggle} aria-expanded={expanded}>
        <span className={`pf-cat-light is-${tone}`} aria-hidden="true">{verifying ? '' : full ? GLYPH[fail ? 'fail' : warn ? 'warn' : 'pass'] : ''}</span>
        <span className="pf-cat-name">{name}</span>
        <span className="pf-cat-meta">
          {verifying && <span className="pf-badge is-verifying">{Math.max(0, revealed - start)}/{checks.length}</span>}
          {full && !fail && <span className="pf-badge is-pass">{pass} pass{warn ? ` · ${warn} warn` : ''}</span>}
          {full && fail > 0 && <span className="pf-badge is-fail">{fail} fail{warn ? ` · ${warn} warn` : ''}</span>}
          {!begun && active && <span className="pf-badge is-verifying">running</span>}
          <span className={`pf-chev${expanded ? ' is-open' : ''}`} aria-hidden="true">▾</span>
        </span>
      </button>
      {!expanded && full && (
        <div className="pf-pips" aria-hidden="true">
          {checks.map((c, i) => <span key={i} className={`pf-pip is-${c.status}`} title={c.name}>{GLYPH[c.status] || '·'}</span>)}
        </div>
      )}
      {expanded && visible.length > 0 && (
        <ol className="pf-checks" ref={listRef} start={offset + 1}>
          {visible.map((c, i) => {
            const latest = offset + i === revealed - 1;
            return (
              <li key={i} className={`pf-check is-${c.status}${latest ? ' is-latest' : ''}`}>
                <span className="pf-check-num">{String(offset + i + 1).padStart(String(total).length, ' ')}</span>
                <span className={`pf-check-glyph is-${c.status}`} aria-label={c.status}>{GLYPH[c.status] || '·'}</span>
                <span className="pf-check-name">{c.name}</span>
                {c.detail && <span className="pf-check-detail" title={c.detail}>{c.detail}</span>}
              </li>
            );
          })}
        </ol>
      )}
    </li>
  );
}

export default function PreflightPanel({ report, running = false, instant = false, compact = false }) {
  const [manual, setManual] = useState({});
  const checks = useMemo(() => toArr(report?.checks), [report?.checks]);
  const { cats, byCat } = useMemo(() => {
    const g = {};
    for (const c of checks) { const k = c.category || 'Other'; (g[k] = g[k] || []).push(c); }
    return { cats: Object.keys(g).sort((a, b) => sortKey(a) - sortKey(b)), byCat: g };
  }, [checks]);
  const boundaries = useMemo(() => {
    const s = new Set(); let o = 0;
    for (const c of cats) { if (o > 0) s.add(o); o += byCat[c].length; }
    return s;
  }, [cats, byCat]);
  const revealed = useProgressiveReveal(checks.length, boundaries, instant);
  const revealDone = checks.length > 0 && revealed >= checks.length;
  const auto = useAutoFold(cats, byCat, revealed);
  const isOpen = useCallback((cat) => (manual[cat] !== undefined ? manual[cat] : auto[cat] !== undefined ? auto[cat] : false), [manual, auto]);
  const anyOpen = cats.some(isOpen);
  const setAll = (v) => setManual(Object.fromEntries(cats.map((c) => [c, v])));

  if (!report && !running) return null;

  if (!report || !checks.length) {
    return (
      <section className={`pf-panel is-waiting${compact ? ' is-compact' : ''}`} aria-live="polite" data-nova-note="Pre-flight checklist waiting for the first check">
        <header className="pf-head">
          <span className="pf-spinner" aria-hidden="true" />
          <h3 className="pf-title">Pre-flight</h3>
          <span className="pf-status is-verifying">starting the check container</span>
        </header>
        <ul className="pf-skeleton">
          {CATEGORY_ORDER.slice(0, compact ? 5 : 10).map((c, i) => <li key={c} style={{ animationDelay: `${i * 110}ms` }}>{c}</li>)}
        </ul>
      </section>
    );
  }

  const totalChecks = report.totalChecks || checks.length;
  const passed = report.totalPassed || 0;
  const failed = report.totalFailed || 0;
  const warned = report.totalWarnings || 0;
  const done = !!report.completedAt || !!report.verdict;
  const hasFail = failed > 0 || (report.verdict && report.verdict !== 'GO');
  const completed = passed + failed + warned;
  const pct = totalChecks ? Math.min(100, (Math.min(revealed, completed) / totalChecks) * 100) : 0;
  const state = !revealDone || !done ? 'verifying' : hasFail ? 'fail' : 'pass';
  const statusText = state === 'verifying' ? `verifying ${Math.min(revealed, completed)}/${totalChecks}` : hasFail ? 'NO-GO' : 'GO';
  let offset = 0;
  const offsets = {};
  for (const c of cats) { offsets[c] = offset; offset += byCat[c].length; }

  return (
    <section className={`pf-panel is-${state}${compact ? ' is-compact' : ''}`} aria-live="polite"
      data-nova-note={`Pre-flight ${statusText}: ${passed} pass, ${warned} warn, ${failed} fail of ${totalChecks}${report.estimatedUsd != null ? `; estimated $${Number(report.estimatedUsd).toFixed(2)}` : ''}`}>
      <header className="pf-head">
        {state === 'verifying' ? <span className="pf-spinner" aria-hidden="true" /> : <span className={`pf-verdict-dot is-${state}`} aria-hidden="true" />}
        <h3 className="pf-title">Pre-flight</h3>
        <span className={`pf-status is-${state}`}>{statusText}</span>
        <span className="pf-counts">
          <span className="is-pass">{passed} pass</span>
          {warned > 0 && <span className="is-warn">{warned} warn</span>}
          {failed > 0 && <span className="is-fail">{failed} fail</span>}
        </span>
        <button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => setAll(!anyOpen)}>{anyOpen ? 'Collapse all' : 'Expand all'}</button>
      </header>
      <div className="pf-progress" role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
        <div className={`pf-progress-fill is-${state}`} style={{ width: `${pct}%` }} />
      </div>
      <ul className="pf-cats">
        {cats.map((cat) => (
          <Category key={cat} name={cat} checks={byCat[cat]} offset={offsets[cat]} total={totalChecks} revealed={revealed}
            expanded={isOpen(cat)} onToggle={() => setManual((m) => ({ ...m, [cat]: !isOpen(cat) }))} active={report.currentCategory === cat && !done} />
        ))}
      </ul>
      {done && revealDone && (
        <footer className={`pf-summary is-${hasFail ? 'fail' : 'pass'}`}>
          <strong className="pf-callsign">{hasFail ? 'NO-GO' : 'ALL SYSTEMS GO'}</strong>
          <span>
            {hasFail ? `${failed} check${failed === 1 ? '' : 's'} failed — resolve before the GPU stage` : `${passed} checks confirmed — cleared for training`}
            {warned > 0 ? ` · ${warned} advisory` : ''}
            {report.estimatedUsd != null ? ` · est. $${Number(report.estimatedUsd).toFixed(2)}${report.estimatedHours != null ? ` / ${Math.round(Number(report.estimatedHours) * 60)} min` : ''}` : ''}
          </span>
          {hasFail && toArr(report.suggestions).length > 0 && (
            <ul className="pf-suggestions">
              {toArr(report.suggestions).map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          )}
        </footer>
      )}
    </section>
  );
}
